import { CheckoutItemInputSchema } from "./checkout";
import { MediaSourceIdSchema } from "../media-reference";
import { StorefrontOfferSchema } from "../offers";
import * as z from "zod";
import { ProductCustomerReviewsSchema, ProductFaqItemSchema } from "../primitives/legacy-sections";
import { CUSTOM_LOGIC_EVENT_HANDLER_PROPS } from "./custom-logic-event-handler-props";
import {
  StorefrontMediaFileSchema,
  StorefrontPageProductDataSchema,
  StorefrontPageSiblingOptionSchema,
  StorefrontShopSchema,
} from "./storefront";

export { CUSTOM_LOGIC_EVENT_HANDLER_PROPS, type CustomLogicEventHandlerProp } from "./custom-logic-event-handler-props";

/**
 * Schema-backed custom-logic contract for Generic sections.
 * The runtime executor in storefront-ui adds the `React` argument on top of this contract.
 */

export const CustomLogicStateSchema = z
  .record(z.string(), z.unknown())
  .describe("Plain object returned by custom logic and exposed to JSX through `$state`.");
export type CustomLogicState = z.infer<typeof CustomLogicStateSchema>;

export const CustomLogicPropsSchema = z
  .record(z.string(), z.unknown())
  .describe("Merchant-authored `exposedProps` values passed into custom logic as `props`.");
export type CustomLogicProps = z.infer<typeof CustomLogicPropsSchema>;

export const CustomLogicEventHandlerPropSchema = z
  .enum(CUSTOM_LOGIC_EVENT_HANDLER_PROPS)
  .describe("Supported React event handler prop names that Generic custom logic may return and bind through `$state`.");

export const RuntimeProductDataSchema = StorefrontPageProductDataSchema.shape.data.describe(
  "Merged product data available on runtime product snapshots. It may include virtual product data, funnel product data, and cached backend product data.",
);
export type RuntimeProductData = z.infer<typeof RuntimeProductDataSchema>;

export const RuntimeMoneySchema = z
  .object({
    amount: z.number().describe("Numeric money amount in the shop currency."),
    currencyCode: z.string().describe("ISO currency code for the money value."),
  })
  .describe("Normalized money snapshot exposed to custom logic.");
export type RuntimeMoney = z.infer<typeof RuntimeMoneySchema>;

export const RuntimeImageLikeSchema = z
  .object({
    url: z.string().nullish().describe("Image URL when one is available."),
    altText: z.string().nullish().describe("Alternative text when one is available."),
  })
  .catchall(z.unknown())
  .describe("Loose image snapshot used by product and variant image fields.");
export type RuntimeImageLike = z.infer<typeof RuntimeImageLikeSchema>;

export const RuntimeProductDataMediaItemSchema = z
  .object({
    id: z.number().describe("Internal media id."),
    url: z.string().describe("Resolved media URL."),
    alt: z.string().optional().describe("Alternative text for the media item."),
    type: z.string().optional().describe("Media type label from the product data layer."),
    thumbnailUrl: z.string().optional().describe("Thumbnail URL for video or preview media."),
    shopId: z.number().optional().describe("Owning shop id when present."),
    key: z.string().optional().describe("Storage key when present."),
  })
  .describe("Normalized product-data media snapshot.");
export type RuntimeProductDataMediaItem = z.infer<typeof RuntimeProductDataMediaItemSchema>;

export const RuntimeResolvedProductMediaItemSchema = z
  .object({
    url: z.string().describe("Resolved media URL."),
    type: z.enum(["image", "video"]).describe("Resolved storefront media type."),
    thumbnailUrl: z.string().optional().describe("Thumbnail URL for video media."),
    alt: z.string().optional().describe("Alternative text for the media item."),
  })
  .describe("Variant-aware media item returned by `hooks.useRuntimeProducts(...).getProductMedia`.");
export type RuntimeResolvedProductMediaItem = z.infer<typeof RuntimeResolvedProductMediaItemSchema>;

export const RuntimeVariantSelectedOptionSchema = z
  .object({
    name: z.string().describe("Product option name."),
    value: z.string().describe("Chosen value for the option."),
  })
  .describe("Single selected option on a variant.");
export type RuntimeVariantSelectedOption = z.infer<typeof RuntimeVariantSelectedOptionSchema>;

export const RuntimeVariantSnapshotSchema = z
  .object({
    externalId: z.string().describe("Variant external id."),
    id: z.string().describe("Alias of the variant external id for JSX/runtime convenience."),
    title: z.string().describe("Variant title."),
    options: RuntimeVariantSelectedOptionSchema.array().describe("Original selected option list."),
    selectedOptions: z
      .record(z.string(), z.string())
      .describe("Selected options keyed by option name for easier lookup."),
    price: z.number().describe("Variant price amount."),
    priceMoney: RuntimeMoneySchema.describe("Variant price with currency metadata."),
    compareAtPrice: z.number().nullable().describe("Variant compare-at price amount, or `null`."),
    compareAtPriceMoney: RuntimeMoneySchema.nullable().describe(
      "Variant compare-at price with currency metadata, or `null`.",
    ),
    image: RuntimeImageLikeSchema.nullish().describe("Variant image when one exists."),
    availableForSale: z.boolean().optional().describe("Whether the variant is currently available for sale."),
  })
  .catchall(z.unknown())
  .describe("Normalized runtime variant snapshot.");
export type RuntimeVariantSnapshot = z.infer<typeof RuntimeVariantSnapshotSchema>;

export const RuntimeProductOptionSchema = z
  .object({
    name: z.string().describe("Normalized option name."),
    values: z.array(z.string()).describe("Available values for the option."),
    originalName: z.string().optional().describe("Original upstream option name when it differs from `name`."),
  })
  .describe("Normalized product option snapshot.");
export type RuntimeProductOption = z.infer<typeof RuntimeProductOptionSchema>;

export const RuntimeProductSnapshotSchema = z
  .object({
    commerceProduct: z
      .record(z.string(), z.unknown())
      .describe(
        "Raw commerce product preserved for internal helper reuse. Custom logic should prefer the typed helpers.",
      ),
    externalId: z.string().describe("Product external id."),
    id: z.string().describe("Alias of the product external id for JSX/runtime convenience."),
    title: z.string().describe("Product title."),
    handle: z.string().describe("Product handle."),
    vendor: z.string().describe("Product vendor."),
    options: RuntimeProductOptionSchema.array().describe("Normalized option snapshots."),
    variants: RuntimeVariantSnapshotSchema.array().describe("Normalized variant snapshots."),
    images: RuntimeImageLikeSchema.array().describe("Product images from commerce data."),
    featuredImage: RuntimeImageLikeSchema.nullish().describe("Featured product image when available."),
    media: z
      .array(z.record(z.string(), z.unknown()))
      .optional()
      .describe("Raw media collection from the commerce product when present."),
    sellingPlanGroups: z.array(z.unknown()).describe("Selling plan groups from the commerce product."),
    description: z.string().optional().describe("Plain text product description when available."),
    descriptionHtml: z.string().optional().describe("HTML product description when available."),
    availableForSale: z.boolean().optional().describe("Whether the product is currently available for sale."),
    requiresSellingPlan: z.boolean().optional().describe("Whether this product requires a subscription."),
    featuredImageUrl: z.string().optional().describe("Convenience featured image URL resolved for product sections."),
    priceMin: z.number().optional().describe("Minimum product price amount."),
    priceMax: z.number().optional().describe("Maximum product price amount."),
    productData: RuntimeProductDataSchema.optional().describe("Merged product data for the product."),
    productDataMedia: RuntimeProductDataMediaItemSchema.array().describe("Normalized product-data media items."),
    mainMedia: RuntimeProductDataMediaItemSchema.nullable().describe("Resolved main media item, or `null`."),
    storyMedia: RuntimeProductDataMediaItemSchema.array().describe("Resolved product story media items."),
    reviews: ProductCustomerReviewsSchema.optional().describe(
      "Normalized product review summary used by buy boxes. Read the rating from `product.reviews.rating` and its adjacent text from `product.reviews.text`.",
    ),
  })
  .catchall(z.unknown())
  .describe("Normalized product snapshot returned by `hooks.useRuntimeProducts(...)`.");
export type RuntimeProductSnapshot = z.infer<typeof RuntimeProductSnapshotSchema>;

export const RuntimeProductListInputSchema = z
  .array(z.union([z.string(), RuntimeProductSnapshotSchema]))
  .nullish()
  .describe("Optional product list input containing external ids or runtime product snapshots.");
export type RuntimeProductListInput = z.infer<typeof RuntimeProductListInputSchema>;

export const RuntimeProductOrderItemSchema = z
  .union([
    z.string().describe("External product id to resolve with the default variant and selling plan."),
    z
      .object({
        productId: z.string().describe("External product id to resolve."),
        variantId: z.string().optional().describe("Optional explicit variant external id."),
        sellingPlanId: z.string().optional().describe("Optional explicit selling plan id."),
      })
      .describe("Manual ordered product entry with optional variant and selling-plan overrides."),
  ])
  .describe("Single item in a manual product order list.");
export type RuntimeProductOrderItem = z.infer<typeof RuntimeProductOrderItemSchema>;

export const RuntimeProductFaqOptionsSchema = z
  .object({
    faqItems: z
      .array(ProductFaqItemSchema)
      .optional()
      .describe(
        "Optional FAQ items override to pass into the shared FAQ resolver. Defaults to product data FAQ items.",
      ),
    withDescription: z
      .boolean()
      .optional()
      .describe("When true, include the product description in the resolved FAQ content."),
  })
  .describe("Options for `hooks.useRuntimeProducts(...).getProductFaqItems`.");
export type RuntimeProductFaqOptions = z.infer<typeof RuntimeProductFaqOptionsSchema>;

export const RuntimeProductFaqItemSchema = z.object({
  value: z.string().describe("Stable accordion item value for this FAQ item."),
  question: z.string().describe("FAQ question text."),
  answer: z.string().describe("FAQ answer text."),
});
export type RuntimeProductFaqItem = z.infer<typeof RuntimeProductFaqItemSchema>;

export const RuntimeProductMediaOptionsSchema = z
  .object({
    variantId: z
      .string()
      .nullish()
      .describe(
        "Optional variant external id. When variant-specific media is configured, getProductMedia returns that curated variant media; otherwise it falls back to the product media/images.",
      ),
  })
  .describe("Options for `hooks.useRuntimeProducts(...).getProductMedia`.");
export type RuntimeProductMediaOptions = z.infer<typeof RuntimeProductMediaOptionsSchema>;

export const RuntimeVariantOptionsInputSchema = z
  .record(z.string(), z.union([z.string(), z.array(z.string())]).nullish())
  .describe(
    "Selected product options keyed by option name. Values may be a single selected value or an array whose first value is used.",
  );
export type RuntimeVariantOptionsInput = z.infer<typeof RuntimeVariantOptionsInputSchema>;

export const RuntimeVariantOptionValuesInputSchema = z
  .array(z.string().nullish())
  .describe(
    "Selected variant option values without option names. Empty, null, or undefined values are ignored. Useful when custom logic knows values like `Capsules` or `With Melatonin` but does not need to know whether the option is named Form, Type, Flavor, or another merchant-defined name.",
  );
export type RuntimeVariantOptionValuesInput = z.infer<typeof RuntimeVariantOptionValuesInputSchema>;

export const RuntimeProductOptionValueSchema = z
  .object({
    value: z.string().describe("Option value."),
    label: z.string().describe("Display label for the option value."),
    isSelected: z.boolean().describe("Whether this value matches the selected option value."),
    isAvailable: z
      .boolean()
      .describe(
        "Whether at least one available variant would match this value together with the other selected options.",
      ),
  })
  .describe("Resolved product option value for buy-box option pickers.");
export type RuntimeProductOptionValue = z.infer<typeof RuntimeProductOptionValueSchema>;

export const RuntimeProductOptionValuesOptionsSchema = z
  .object({
    selectedOptions: RuntimeVariantOptionsInputSchema.optional().describe(
      "Current selected options. The requested option is replaced with each candidate value to calculate availability.",
    ),
    hideUnavailable: z.boolean().optional().describe("When true, omit unavailable values from the result."),
  })
  .describe("Options for `hooks.useRuntimeProducts(...).getOptionValues`.");
export type RuntimeProductOptionValuesOptions = z.infer<typeof RuntimeProductOptionValuesOptionsSchema>;

export const RuntimeSellingPlanOptionSchema = z
  .object({
    id: z.string().describe("Selling plan id."),
    name: z.string().describe("Raw selling plan name."),
    label: z.string().describe("Display label for the selling plan. Defaults to the raw plan name."),
    groupName: z.string().optional().describe("Owning selling plan group name when available."),
    index: z.number().describe("Zero-based order from the commerce selling plan groups."),
    price: z.number().nullable().describe("Resolved selling-plan price for the selected variant."),
    compareAtPrice: z.number().nullable().describe("Resolved compare-at price for the selected variant and plan."),
    perDeliveryPrice: z.number().nullable().describe("Resolved per-delivery price when available."),
    currencyCode: z.string().nullable().describe("Resolved currency code when available."),
    isSelected: z.boolean().describe("Whether this plan matches the selectedSellingPlanId input."),
    sellingPlan: z.unknown().describe("Raw selling plan object for advanced custom logic."),
    allocation: z.unknown().optional().describe("Raw variant allocation for this selling plan when available."),
  })
  .describe("Normalized selling plan option for custom buy boxes.");
export type RuntimeSellingPlanOption = z.infer<typeof RuntimeSellingPlanOptionSchema>;

export const RuntimeSellingPlanOptionsSchema = z
  .object({
    variantId: z.string().nullish().describe("Variant external id used to scope available selling plans."),
    selectedSellingPlanId: z.string().nullish().describe("Optional selected selling plan id."),
  })
  .describe("Options for `hooks.useRuntimeProducts(...).getSellingPlans`.");
export type RuntimeSellingPlanOptions = z.infer<typeof RuntimeSellingPlanOptionsSchema>;

export const RuntimePriceSummaryInputSchema = z
  .object({
    productId: z.string().nullish().describe("Optional product external id. Falls back to the current product."),
    variantId: z.string().nullish().describe("Optional variant external id. Falls back to the first variant."),
    sellingPlanId: z.string().nullish().describe("Optional selling plan id for subscription pricing."),
    quantity: z.number().optional().describe("Optional quantity multiplier. Defaults to 1."),
  })
  .describe("Input for `hooks.useRuntimeProducts(...).getPriceSummary`.");
export type RuntimePriceSummaryInput = z.infer<typeof RuntimePriceSummaryInputSchema>;

export const RuntimePriceSummarySchema = z
  .object({
    price: z.number().nullable().describe("Unit final price."),
    compareAtPrice: z.number().nullable().describe("Unit compare-at price when available."),
    subtotalPrice: z.number().nullable().describe("Final price multiplied by quantity."),
    subtotalCompareAtPrice: z.number().nullable().describe("Compare-at price multiplied by quantity."),
    savingsAmount: z.number().nullable().describe("Subtotal savings amount when positive."),
    savingsPercentage: z.number().nullable().describe("Percentage savings when positive."),
    currencyCode: z.string().nullable().describe("Resolved variant currency code used to format all price labels."),
    priceLabel: z.string().describe("Formatted unit final price using the resolved currencyCode."),
    compareAtPriceLabel: z.string().describe("Formatted unit compare-at price using the resolved currencyCode."),
    subtotalPriceLabel: z.string().describe("Formatted subtotal final price using the resolved currencyCode."),
    subtotalCompareAtPriceLabel: z
      .string()
      .describe("Formatted subtotal compare-at price using the resolved currencyCode."),
    savingsLabel: z.string().describe("Formatted savings amount using the resolved currencyCode."),
  })
  .describe("Resolved price summary for buy boxes.");
export type RuntimePriceSummary = z.infer<typeof RuntimePriceSummarySchema>;

export const RuntimeSiblingOptionsOptionsSchema = z
  .object({
    hideUnavailable: z.boolean().optional().describe("When true, hide sibling values that are currently unavailable."),
  })
  .describe("Options for `hooks.useRuntimeProducts(...).getSiblingOptions`.");
export type RuntimeSiblingOptionsOptions = z.infer<typeof RuntimeSiblingOptionsOptionsSchema>;

export const RuntimeOrderedProductsResultSchema = z
  .object({
    products: RuntimeProductSnapshotSchema.array().describe("Ordered product snapshots."),
    resolvedProductsOrder: z
      .array(
        z.object({
          item: RuntimeProductOrderItemSchema.describe("Original manual order item."),
          product: RuntimeProductSnapshotSchema.describe("Resolved runtime product snapshot."),
        }),
      )
      .describe("Resolved order entries paired with the matching runtime product."),
    productVariantMap: z
      .record(z.string(), z.string())
      .describe("Map of product external ids to the resolved default variant ids."),
    productSellingPlanMap: z
      .record(z.string(), z.string())
      .describe("Map of product external ids to the resolved default selling plan ids."),
  })
  .describe("Resolved manual product ordering result returned by `hooks.useRuntimeProducts(...).getOrderedProducts`.");
export type RuntimeOrderedProductsResult = z.infer<typeof RuntimeOrderedProductsResultSchema>;

export const RuntimeRouteContextSchema = z
  .object({
    funnelId: z.number().describe("Current funnel id."),
    pageType: z.string().describe("Current page type, usually `HOME`, `PDP`, or `CART`."),
    sectionId: z.string().optional().describe("Current section id when available."),
    locale: z.string().describe("Resolved storefront locale."),
    pathname: z.string().describe("Current browser pathname."),
  })
  .describe("Route-level runtime context exposed to custom logic.");
export type RuntimeRouteContext = z.infer<typeof RuntimeRouteContextSchema>;

export const RuntimeViewerContextSchema = z
  .object({
    countryCode: z.string().nullable().describe("Resolved viewer country code, or `null` when unavailable."),
  })
  .describe("Viewer-level runtime context exposed to custom logic.");
export type RuntimeViewerContext = z.infer<typeof RuntimeViewerContextSchema>;

export const RuntimeSectionContextSchema = z
  .object({
    id: z.string().optional().describe("Current section id when available."),
    selector: z
      .string()
      .optional()
      .describe("The `data-jurni-id` attribute value used to idetify the current section."),
    querySelector: z
      .string()
      .optional()
      .describe(
        "The query selector string that can be used with `document.querySelector` to locate the current section element.",
      ),
    isEditor: z.boolean().describe("Whether the section is running inside the editor."),
  })
  .describe("Section-level runtime context exposed to custom logic.");
export type RuntimeSectionContext = z.infer<typeof RuntimeSectionContextSchema>;

export const CustomLogicFailureSchema = z.object({
  phase: z.enum(["compile", "execute", "uncaught"]),
  sourceUrl: z.string().optional(),
  name: z.string(),
  message: z.string(),
  stack: z.string().optional(),
});
export type CustomLogicFailure = z.infer<typeof CustomLogicFailureSchema>;

export const RuntimeMediaItemSchema = z
  .object({
    id: StorefrontMediaFileSchema.shape.id.describe("Internal media id."),
    url: StorefrontMediaFileSchema.shape.url.describe("Resolved media URL."),
    type: z.string().optional().describe("Media type label from the funnel media record."),
    thumbnailUrl: z.string().optional().describe("Thumbnail URL for preview media."),
    shopId: StorefrontMediaFileSchema.shape.shopId.optional().describe("Owning shop id when present."),
    key: StorefrontMediaFileSchema.shape.key.optional().describe("Storage key when present."),
  })
  .describe("Normalized funnel media or story snapshot exposed through `runtime.store`.");
export type RuntimeMediaItem = z.infer<typeof RuntimeMediaItemSchema>;

export const RuntimeCartLineDiscountSchema = z
  .object({
    amount: RuntimeMoneySchema.describe("Discount amount taken off this line."),
    title: z.string().nullable().describe("Discount title or code, or `null` when unknown."),
    type: z.enum(["automatic", "manual", "code"]).nullable().describe("How the discount was applied, or `null`."),
  })
  .describe("Discount applied to a cart line.");
export type RuntimeCartLineDiscount = z.infer<typeof RuntimeCartLineDiscountSchema>;

export const RuntimeCartLineSchema = z
  .object({
    key: z.string().describe("Unique cart line key."),
    productId: z.string().describe("Product external id."),
    variantId: z.string().describe("Variant external id."),
    title: z.string().describe("Product title."),
    image: RuntimeImageLikeSchema.nullish().describe("Line image when one exists."),
    quantity: z.number().describe("Line quantity."),
    unitPrice: RuntimeMoneySchema.describe("Variant unit price before line discounts."),
    unitCompareAtPrice: RuntimeMoneySchema.nullable().describe("Variant unit compare-at price, or `null`."),
    linePrice: RuntimeMoneySchema.describe("Total line price after discounts. Free gifts are 0."),
    compareAtLinePrice: RuntimeMoneySchema.describe(
      "Line price the cart shows struck through: the compare-at price times quantity when one is set, otherwise the line price before discounts. It can include sale savings, so derive applied cart discounts from `discounts`, not from this value.",
    ),
    sellingPlanId: z.string().nullable().describe("Subscription selling plan id, or `null` for one-time lines."),
    sellingPlanName: z
      .string()
      .nullable()
      .describe("Subscription selling plan name (e.g. `Every 24 Days`), or `null` for one-time lines."),
    options: RuntimeVariantSelectedOptionSchema.array().describe(
      'Selected variant options, e.g. `[{ name: "Size", value: "16oz" }]`.',
    ),
    discounts: RuntimeCartLineDiscountSchema.array().describe("Discounts applied to this line."),
    attributes: z.record(z.string(), z.string()).describe("Line item properties keyed by name."),
    giftOfferIds: z
      .number()
      .array()
      .describe(
        "Ids of the gift with purchase offers in `runtime.store.offers` that added this line. Empty for non-gifts. Offers that award the same gift share one line, so `quantity` is their combined quantity.",
      ),
  })
  .describe("Normalized cart line snapshot returned by `hooks.useCart()`.");
export type RuntimeCartLine = z.infer<typeof RuntimeCartLineSchema>;

export const RuntimeCartSchema = z
  .object({
    currencyCode: z.string().describe("ISO currency code of the cart."),
    itemCount: z.number().describe("Total quantity of the visible cart lines."),
    totalPrice: RuntimeMoneySchema.describe("Cart total after discounts."),
    originalTotalPrice: RuntimeMoneySchema.describe("Cart total before discounts."),
    discountCodes: z
      .object({
        code: z.string().describe("Discount code entered on the cart."),
        applicable: z.boolean().describe("Whether the code currently applies to the cart."),
      })
      .array()
      .describe("Discount codes entered on the cart."),
    lines: RuntimeCartLineSchema.array().describe(
      "Visible cart lines. Products hidden by the cart config are excluded.",
    ),
  })
  .describe("Normalized cart snapshot returned by `hooks.useCart()`.");
export type RuntimeCart = z.infer<typeof RuntimeCartSchema>;

export const RuntimeStoreSchema = z
  .object({
    cart: z.null().describe("Always `null`. Read the reactive cart with `hooks.useCart()`."),
    funnelMedia: RuntimeMediaItemSchema.array().describe("Funnel media snapshots."),
    offers: StorefrontOfferSchema.array().describe(
      "Active funnel offers (gift with purchase, free shipping, discounts, samples...). Match gift lines to an offer with `hooks.useCart().lines[].giftOfferIds`. Empty on native Shopify pages.",
    ),
  })
  .describe("Store-level runtime context exposed to custom logic.");
export type RuntimeStore = z.infer<typeof RuntimeStoreSchema>;

export const CustomComponentRuntimeSchema = z
  .object({
    shop: StorefrontShopSchema.describe("Current funnel shop snapshot."),
    checkoutUrl: z
      .string()
      .describe(
        "Host checkout URL. Author checkout actions with `helpers.navigateToCheckout([...])`, which prepares the selected products and preserves attribution.",
      ),
    route: RuntimeRouteContextSchema.describe("Route-level runtime context."),
    store: RuntimeStoreSchema.describe("Store-level runtime context."),
    viewer: RuntimeViewerContextSchema.describe("Viewer-level runtime context."),
    section: RuntimeSectionContextSchema.describe("Section-level runtime context."),
  })
  .describe("Schema-backed runtime object passed into Generic section custom logic.");
export type CustomComponentRuntime = z.infer<typeof CustomComponentRuntimeSchema>;

const OptionalProductIdInputSchema = z.string().nullish().describe("Optional external product id.");

export const AddSingleProductToCartInputSchema = z.object({
  variantId: z.string(),
  sellingPlanId: z.string().optional(),
  quantity: z.number().optional(),
  lineItemProperties: z
    .array(
      z.object({
        key: z.string(),
        value: z.string(),
      }),
    )
    .optional(),
});
export type AddSingleProductToCartInput = z.infer<typeof AddSingleProductToCartInputSchema>;

export const AddMultipleProductsToCartInputSchema = z
  .array(AddSingleProductToCartInputSchema)
  .describe(
    "Input array of cart line items to add in one batch. Each item includes a variantId and may also include sellingPlanId, quantity, and lineItemProperties.",
  );
export type AddMultipleProductsToCartInput = z.infer<typeof AddMultipleProductsToCartInputSchema>;

export const AddToCartOptionsSchema = z
  .object({
    openCartDrawer: z
      .boolean()
      .optional()
      .describe("When false, adds items without opening the cart drawer. Defaults to true."),
    redirectUrl: z
      .string()
      .optional()
      .describe(
        "Optional URL to navigate to after the cart add succeeds. This value is used exactly as provided. When set, the cart drawer is not opened. For checkout actions, use `helpers.navigateToCheckout([...])`.",
      ),
  })
  .describe("Optional add-to-cart behavior options.");
export type AddToCartOptions = z.infer<typeof AddToCartOptionsSchema>;

const NavigateToCheckoutItemSchema = CheckoutItemInputSchema.extend({
  product: RuntimeProductSnapshotSchema.describe(
    "Runtime product snapshot returned by `hooks.useRuntimeProducts(...)`.getProduct().",
  ),
});
export const NavigateToCheckoutInputSchema = z.union([
  NavigateToCheckoutItemSchema,
  z.array(NavigateToCheckoutItemSchema).min(1),
]);
export type NavigateToCheckoutInput = z.infer<typeof NavigateToCheckoutInputSchema>;

export const CustomLogicHelpersSchema = z
  .object({
    resolveMediaUrl: z
      .function({
        input: [MediaSourceIdSchema.describe("Typed shop media reference, or legacy funnel media ID.")],
        output: z.string().optional(),
      })
      .describe(
        "Function. Resolves a typed shop media reference synchronously, or a legacy funnel media ID when available.",
      ),
    openCart: z
      .function({
        input: [],
        output: z.void(),
      })
      .describe("Function. Opens the active cart surface for the current host."),
    openProductPage: z
      .function({
        input: [
          z.string().describe("External product id to preview or open."),
          z.string().optional().describe("Optional selected variant external id."),
          z.boolean().optional().describe("Optional override for external product-page navigation."),
        ],
        output: z.void(),
      })
      .describe(
        "Function. Opens the storefront product preview or navigates to the product page for the supplied product and optional variant, with an optional external-navigation override.",
      ),
    addMultipleProductsToCart: z
      .function({
        input: [AddMultipleProductsToCartInputSchema, AddToCartOptionsSchema.optional()],
        output: z.promise(z.void()),
      })
      .describe(
        'Async function returning `Promise<void>`. Receives an array of cart line items with `variantId` and optional `sellingPlanId`, `quantity`, and `lineItemProperties`, plus optional `{ openCartDrawer?, redirectUrl? }` options. `lineItemProperties` must be an array of objects with string `key` and `value` fields, e.g. [{ key: "some_key", value: "its_value" }]. Adds all provided items to the cart in one batch. Opens the cart drawer by default unless `openCartDrawer` is false or `redirectUrl` is set. Use this for cart actions. For checkout actions, use `navigateToCheckout([...])`.',
      ),
    navigateToCheckout: z
      .function({
        input: [NavigateToCheckoutInputSchema],
        output: z.promise(z.void()),
      })
      .describe(
        "Async function returning `Promise<void>`. Call with a non-empty array of `{ product, variantId?, sellingPlanId?, quantity? }` objects, including for one product. Include every selected product in one call. Each product is a runtime snapshot from `getProduct(...)`. The runtime validates the selection, prepares checkout, preserves Jurni attribution, and navigates. Use this helper for all checkout actions; do not implement cart mutations or checkout redirects in custom logic. Await it when custom logic needs to keep a loading state active until navigation is triggered. For just adding items to the cart, use `addMultipleProductsToCart`.",
      ),
  })
  .describe("Predefined helper functions passed into custom logic as `helpers`.");

export type CustomLogicHelpers = z.infer<typeof CustomLogicHelpersSchema>;

export type CustomLogicHelperProductListInput = RuntimeProductListInput;
export type CustomLogicHelperProductList = RuntimeProductSnapshot[];
export type CustomLogicHelperProductSnapshot = RuntimeProductSnapshot;
export type CustomLogicOrderedProductsResult = RuntimeOrderedProductsResult;
export type CustomLogicOrderedProductsEntry = CustomLogicOrderedProductsResult["resolvedProductsOrder"][number];

export const CustomLogicRuntimeProductsResultSchema = z
  .object({
    products: RuntimeProductSnapshotSchema.array().describe("Normalized runtime product snapshots."),
    currentProductId: z.string().nullable().describe("Current product external id, or `null` when none is resolved."),
    currentProduct: RuntimeProductSnapshotSchema.nullable().describe(
      "Current product snapshot, or `null` when none is resolved.",
    ),
    isLoading: z.boolean().describe("Whether the requested product set is currently loading for the first time."),
    isFetching: z.boolean().describe("Whether the requested product set is currently fetching in the background."),
    getProduct: z
      .function({
        input: [OptionalProductIdInputSchema],
        output: RuntimeProductSnapshotSchema.nullable(),
      })
      .describe(
        "Function. Receives an optional external product id. Returns the matching runtime product snapshot, or falls back to the current product when the id is omitted.",
      ),
    getProducts: z
      .function({
        input: [RuntimeProductListInputSchema],
        output: RuntimeProductSnapshotSchema.array(),
      })
      .describe(
        "Function. Receives an optional list of external product ids or runtime product snapshots. Returns normalized runtime product snapshots.",
      ),
    getProductByHandle: z
      .function({
        input: [z.string().describe("Product handle.")],
        output: RuntimeProductSnapshotSchema.nullable(),
      })
      .describe(
        "Function. Receives a product handle. Returns the matching runtime product snapshot, or `null` when none is found.",
      ),
    getVariantByOptionValues: z
      .function({
        input: [OptionalProductIdInputSchema, RuntimeVariantOptionValuesInputSchema],
        output: RuntimeVariantSnapshotSchema.nullable(),
      })
      .describe(
        "Function. Receives an optional product external id and an array of selected option values without option names, for example `['Capsules', 'With Melatonin']`. Returns the first variant whose real option values match all provided values. Use this for compact buy-box logic when the UI state is value-based and option names are not important.",
      ),
    getOptionValues: z
      .function({
        input: [
          OptionalProductIdInputSchema,
          z.string().describe("Product option name."),
          RuntimeProductOptionValuesOptionsSchema.optional(),
        ],
        output: RuntimeProductOptionValueSchema.array(),
      })
      .describe(
        "Function. Receives an optional product external id, option name, and optional `{ selectedOptions?, hideUnavailable? }`. Returns option values with `isSelected` and `isAvailable` flags for buy-box pickers. Use this to render variant option buttons or swatches.",
      ),
    getVisibleProducts: z
      .function({
        input: [RuntimeProductListInputSchema],
        output: RuntimeProductSnapshotSchema.array(),
      })
      .describe(
        "Function. Receives an optional list of external product ids or runtime product snapshots. Returns runtime product snapshots after applying the storefront sibling visibility rules used by product sections.",
      ),
    getOrderedProducts: z
      .function({
        input: [
          z.array(RuntimeProductOrderItemSchema).nullish().describe("Optional manual product order configuration."),
        ],
        output: RuntimeOrderedProductsResultSchema,
      })
      .describe(
        "Function. Receives an optional manual product order array of external product ids or `{ productId, variantId?, sellingPlanId? }` items. Returns the resolved ordered products result with products, resolved order entries, and the product-to-variant and product-to-selling-plan maps.",
      ),
    getSellingPlans: z
      .function({
        input: [OptionalProductIdInputSchema, RuntimeSellingPlanOptionsSchema.optional()],
        output: RuntimeSellingPlanOptionSchema.array(),
      })
      .describe(
        "Function. Receives an optional product external id and optional `{ variantId?, selectedSellingPlanId? }`. Returns selling plans available for the selected variant, with prices and `isSelected`. If the result is empty, treat the product/variant as having no subscription options.",
      ),
    getPriceSummary: z
      .function({
        input: [RuntimePriceSummaryInputSchema.optional()],
        output: RuntimePriceSummarySchema,
      })
      .describe(
        "Function. Receives optional `{ productId?, variantId?, sellingPlanId?, quantity? }`. Omit sellingPlanId for one-time pricing; pass sellingPlanId for subscription pricing. Returns numeric prices, formatted labels, currencyCode, and savings.",
      ),
    getProductFaqItems: z
      .function({
        input: [OptionalProductIdInputSchema, RuntimeProductFaqOptionsSchema.optional()],
        output: z.array(RuntimeProductFaqItemSchema),
      })
      .describe(
        "Function. Receives an optional external product id and optional `{ faqItems?, withDescription? }` options. Returns resolved FAQ items with `value`, `question`, and `answer` from product data, unless `faqItems` is provided as an override.",
      ),
    getProductMedia: z
      .function({
        input: [OptionalProductIdInputSchema, RuntimeProductMediaOptionsSchema.optional()],
        output: RuntimeResolvedProductMediaItemSchema.array(),
      })
      .describe(
        "Function. Receives an optional external product id and optional `{ variantId? }` options. Returns the best gallery for the selected variant: configured variant media when present, otherwise product media/images. Does not manually add the variant image.",
      ),
    getProductDescriptionText: z
      .function({
        input: [OptionalProductIdInputSchema],
        output: z.string(),
      })
      .describe(
        "Function. Receives an optional external product id and returns the product description as plain text. Use this instead of stripping descriptionHtml manually.",
      ),
    getSiblingOptions: z
      .function({
        input: [OptionalProductIdInputSchema, RuntimeSiblingOptionsOptionsSchema.optional()],
        output: StorefrontPageSiblingOptionSchema.array().optional(),
      })
      .describe(
        "Function. Receives an optional external product id and optional `{ hideUnavailable? }` options. Returns the sibling option groups for that product, or `undefined` when none exist.",
      ),
  })
  .describe("Result returned by `hooks.useRuntimeProducts`.");
export type CustomLogicRuntimeProductsResult = z.infer<typeof CustomLogicRuntimeProductsResultSchema>;

export const CustomLogicHooksSchema = z
  .object({
    useQuery: z
      .function()
      .describe(
        "TanStack Query v5 useQuery(options), using the host's existing QueryClient. Receives standard query options including queryKey, queryFn, enabled, staleTime and retry. queryFn receives { queryKey, signal } and must return data or a promise, throwing on failure. Returns the standard query result including data, error, isPending, isFetching and refetch. Call unconditionally at the top level of custom logic. Custom logic owns the request and cache key; include the shop, market and every request parameter in the key. No commerce client or credentials are injected by this hook.",
      ),
    useInfiniteQuery: z
      .function()
      .describe(
        "TanStack Query v5 useInfiniteQuery(options), using the host's existing QueryClient. Receives standard infinite-query options including queryKey, queryFn, initialPageParam and getNextPageParam. queryFn receives { pageParam, queryKey, signal }. getNextPageParam returns the next cursor/page or undefined/null when finished. Returns data.pages, data.pageParams, fetchNextPage, hasNextPage, isFetchingNextPage, error and the other standard query fields. Call unconditionally at the top level of custom logic. Custom logic owns fetching, page interpretation and a cache key scoped to the shop, market and filters. Use a distinct key from useQuery for the same resource.",
      ),
    useProductSectionInViewport: z
      .function({
        input: [],
        output: z.boolean(),
      })
      .describe(
        "Hook. Returns whether any section with a configured product exposed prop is currently visible in the viewport. Call it once at the top level of custom logic and never conditionally.",
      ),
    useCartCount: z
      .function({
        input: [],
        output: z.number(),
      })
      .describe(
        "Hook. Returns the current visible cart item quantity count for the active storefront host. Call it once at the top level of custom logic and never conditionally.",
      ),
    useCart: z
      .function({
        input: [],
        output: RuntimeCartSchema.nullable(),
      })
      .describe(
        "Hook. Returns the reactive cart snapshot `{ currencyCode, itemCount, totalPrice, originalTotalPrice, discountCodes, lines }`, where money values are `{ amount, currencyCode }` and each line has `key`, `productId`, `variantId`, `title`, `image`, `quantity`, `unitPrice`, `unitCompareAtPrice`, `linePrice`, `compareAtLinePrice`, `sellingPlanId`, `sellingPlanName`, `options`, `discounts`, `attributes` and `giftOfferIds`. Returns `null` while the cart loads and on native Shopify pages. Re-renders when the cart changes. Call it once at the top level of custom logic and never conditionally.",
      ),
    useRuntimeProducts: z
      .function({
        input: [RuntimeProductListInputSchema],
        output: CustomLogicRuntimeProductsResultSchema,
      })
      .describe(
        "Hook. Receives an optional list of external product ids or runtime product snapshots. Returns normalized runtime product snapshots plus scoped product accessors and loading state. Call it once at the top level of custom logic and never conditionally.",
      ),
  })
  .describe("Predefined hook utilities passed into custom logic as `hooks`.");
export type CustomLogicHooks = z.infer<typeof CustomLogicHooksSchema>;

export const CustomLogicContextSchema = z
  .object({
    runtime: CustomComponentRuntimeSchema.describe("Schema-backed runtime object available to custom logic."),
    props: CustomLogicPropsSchema.describe("Merchant-authored exposed props passed into custom logic."),
    helpers: CustomLogicHelpersSchema.describe("Predefined helper functions passed into custom logic."),
    hooks: CustomLogicHooksSchema.describe("Predefined hook utilities passed into custom logic."),
  })
  .describe("Schema-backed custom-logic execution context. The runtime executor adds `React` on top of this context.");

export interface CustomLogicContext<TProps extends CustomLogicProps = CustomLogicProps> {
  runtime: CustomComponentRuntime;
  props: TProps;
  helpers: CustomLogicHelpers;
  hooks: CustomLogicHooks;
}
